import { zodResolver } from "@hookform/resolvers/zod";
import { Decimal } from "decimal.js";
import React from "react";
import {
  Autocomplete,
  ListLayout,
  useFilter,
  Virtualizer,
} from "react-aria-components";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import type { z } from "zod";

import * as resultSchemas from "@blis/shared/schemas/result";
import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CommandBoxInput,
  CommandBoxList,
  CommandBoxPopover,
} from "@/components/ui/command-box-rac";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FieldError,
  Input,
  Label,
  TextArea,
  TextField,
} from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { Note } from "@/components/ui/note";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import { useDialogState } from "../detail/state";

type Result = RouterOutputs["results"]["get"];
type AttributeSpec = Result["testResultSpec"]["attributeSpecs"][number];

export function UpdateResultDialog() {
  const [state, setState] = useDialogState("updateTestResult");

  const result = trpc.results.get.useQuery(
    { id: state?.resultId },
    { enabled: !!state?.resultId },
  );

  // Get accession ID from result query
  const accessionId = result.data?.testOrder?.accessionId;

  const accessionSamples = trpc.samples.byAccession.useQuery(
    { accessionId: accessionId || 0 },
    { enabled: !!accessionId },
  );

  return (
    <Modal isOpen={!!state} onOpenChange={() => setState(null)}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Update result</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {result.data && (
            <ResultFormContext.Provider
              value={{
                result: result.data,
                accessionSamples: accessionSamples.data || [],
              }}
            >
              <UpdateResultForm
                onSuccess={() => setState(null)}
                result={result.data}
              />
            </ResultFormContext.Provider>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="submit" form="update-result-form">
            Submit
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

type AccessionSample = {
  id: number;
  displayId: string | null;
  tube: { id: number; name: string } | null;
  transfusion: unknown | null;
  tubeType: unknown | null;
};

type ResultFormContextType = {
  result: Result;
  accessionSamples: AccessionSample[];
};
const ResultFormContext = React.createContext<ResultFormContextType>(
  {} as ResultFormContextType,
);

type FormValues = z.infer<typeof resultSchemas.update>;

function UpdateResultForm({
  onSuccess,
  formId = "update-result-form",
  result,
}: {
  onSuccess: () => void;
  formId?: string;
  result: Result;
}) {
  const updateResult = trpc.results.update.useMutation(
    mutationResponse({
      errorTitle: "Error updating result",
      successMessage: "Result updated",
      onSuccess,
    }),
  );

  // Build attributes array with existing values
  const existingAttributes = result.testResultSpec.attributeSpecs.map(
    (spec) => {
      const existingValue = result.attributes.find(
        (attr) => attr.attributeSpecId === spec.id,
      );

      return {
        attributeSpecId: spec.id,
        textValue: existingValue?.textValue ?? null,
        numericValue: existingValue?.numericValue ?? null,
        booleanValue: existingValue?.booleanValue ?? null,
      };
    },
  );

  const methods = useForm({
    resolver: zodResolver(resultSchemas.update),
    defaultValues: {
      id: result.id,
      resultId: result.resultId || undefined,
      resultFreeText: result.resultFreeText || undefined,
      resultHla: result.resultHla || undefined,
      resultNumeric: result.resultNumeric || undefined,
      status: result.status as "initial" | "final",
      attributes: existingAttributes,
      comment: result.comment || undefined,
      auditReason: "",
      sampleId: result.sample?.id || undefined,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    updateResult.mutate(data);
  };

  return (
    <form
      id={formId}
      onSubmit={methods.handleSubmit(onSubmit)}
      className="flex flex-col gap-2"
    >
      <FormProvider {...methods}>
        <TestDetails />
        <ResultEntry />
        <ResultStatus />
        <ResultAttributes />
        <SampleSelect />
        <CommentInput />
        <AuditReasonTextArea />
        <Note className="my-1" intent="info">
          This is a corrective action. Workflow events, such as reflexes, will
          not be triggered.
        </Note>
      </FormProvider>
    </form>
  );
}

function SampleSelect() {
  const { control } = useFormContext<FormValues>();
  const { accessionSamples } = React.useContext(ResultFormContext);

  const options = accessionSamples
    .map((sample) => ({
      id: sample.id,
      displayId: sample.displayId || sample.id.toString(),
      tube: sample.tube,
    }))
    .sort((a, b) => a.displayId.localeCompare(b.displayId));

  return (
    <Controller
      control={control}
      name="sampleId"
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <Select
          selectedKey={field.value ?? null}
          onSelectionChange={(key) => field.onChange(key)}
          validationBehavior="aria"
          isInvalid={!!error}
        >
          <Label>Sample used</Label>
          <SelectTrigger className="truncate" />
          <SelectList items={options}>
            {(option) => (
              <SelectItem id={option.id} textValue={option.displayId}>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" size="sm">
                    {option.displayId}
                  </Badge>
                  <Text size="2" className="truncate">
                    {option.tube?.name}
                  </Text>
                </div>
              </SelectItem>
            )}
          </SelectList>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function TestDetails() {
  const { result } = React.useContext(ResultFormContext);
  return (
    <div className="flex items-center gap-2 mb-2">
      <Label>Test</Label>
      <Text muted>{result.testResultSpec.name}</Text>
    </div>
  );
}

const statusOptions = [
  { label: "Initial", value: "initial" },
  { label: "Final", value: "final" },
];

function ResultStatus() {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="status"
      render={({ field }) => (
        <Select selectedKey={field.value} onSelectionChange={field.onChange}>
          <Label>Status</Label>
          <SelectTrigger />
          <SelectList items={statusOptions}>
            {(option) => (
              <SelectItem id={option.value} textValue={option.label}>
                {option.label}
              </SelectItem>
            )}
          </SelectList>
        </Select>
      )}
    />
  );
}

function ResultEntry() {
  const { result } = React.useContext(ResultFormContext);
  switch (result.testResultSpec.type) {
    case "TEXT":
      return <EnumResultEntry />;
    case "NUMERIC":
      return <NumericResultEntry />;
    case "HLA":
      return <HLAResultEntry />;
    case "FREE_TEXT":
      return <FreeTextResultEntry />;
    case "ORGANISM":
      return <OrganismResultEntry />;
    default:
      return null;
  }
}

function EnumResultEntry() {
  const { result } = React.useContext(ResultFormContext);
  const { control } = useFormContext<FormValues>();

  const options = result.testResultSpec.testResults.map(({ testResult }) => ({
    label: testResult.description,
    value: testResult.value,
  }));

  return (
    <Controller
      control={control}
      name="resultId"
      render={({ field }) => (
        <Select selectedKey={field.value} onSelectionChange={field.onChange}>
          <Label>Result</Label>
          <SelectTrigger />
          <SelectList items={options}>
            {(option) => (
              <SelectItem id={option.value} textValue={option.label}>
                {option.label}
              </SelectItem>
            )}
          </SelectList>
        </Select>
      )}
    />
  );
}

function NumericResultEntry() {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="resultNumeric"
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          value={field.value?.toString() || ""}
          onChange={(value) =>
            field.onChange(value ? new Decimal(value) : null)
          }
          validationBehavior="aria"
          isInvalid={!!error}
        >
          <Label>Result</Label>
          <Input type="number" />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function FreeTextResultEntry() {
  const { result } = React.useContext(ResultFormContext);
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="resultFreeText"
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          value={result.resultFreeText || ""}
          validationBehavior="aria"
          isInvalid={!!error}
        >
          <Label>Result</Label>
          <TextArea />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function HLAResultEntry() {
  const { result } = React.useContext(ResultFormContext);
  return (
    <div className="flex items-center gap-1">
      <Label>Result</Label>
      <Text>{result.resultHla?.join(", ")}</Text>
    </div>
  );
}

function OrganismResultEntry() {
  const { control } = useFormContext<FormValues>();
  const orgsnisms = trpc.results.organismResults.useQuery(undefined, {
    staleTime: 1000 * 60 * 60 * 24,
  });
  const layout = React.useMemo(() => new ListLayout(), []);
  const filter = useFilter({ sensitivity: "base" });
  return (
    <Controller
      control={control}
      name="resultFreeText"
      render={({ field, fieldState: { error } }) => (
        <Select
          {...field}
          selectedKey={field.value}
          onSelectionChange={field.onChange}
          isInvalid={!!error}
          validationBehavior="aria"
        >
          <Label>Result</Label>
          <SelectTrigger />
          <CommandBoxPopover className="max-w-[300px]">
            <Autocomplete filter={filter.contains}>
              <CommandBoxInput />
              <Virtualizer layout={layout}>
                <CommandBoxList
                  items={orgsnisms.data?.map((org) => ({
                    label: org.name,
                    value: org.code,
                  }))}
                  className="max-h-[200px]"
                >
                  {(option) => (
                    <SelectItem id={option.label} textValue={option.label}>
                      {option.label}
                    </SelectItem>
                  )}
                </CommandBoxList>
              </Virtualizer>
            </Autocomplete>
          </CommandBoxPopover>
          <FieldError>{error?.message}</FieldError>
        </Select>
      )}
    />
  );
}

function CommentInput() {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="comment"
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          validationBehavior="aria"
          isInvalid={!!error}
          className="w-full"
        >
          <Label>Result comment</Label>
          <Input />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function AuditReasonTextArea() {
  const { control } = useFormContext<FormValues>();
  return (
    <Controller
      control={control}
      name="auditReason"
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          validationBehavior="aria"
          isInvalid={!!error}
          className="w-full"
        >
          <Label>Reason for change</Label>
          <TextArea />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function ResultAttributes() {
  const { result } = React.useContext(ResultFormContext);

  return result.testResultSpec.attributeSpecs.map((spec) => {
    switch (spec.type) {
      case "TEXT":
        return <TextResultAttribute key={spec.id} spec={spec} />;
      case "NUMERIC":
        return <NumericResultAttribute key={spec.id} spec={spec} />;
      case "BOOLEAN":
        return <BooleanResultAttribute key={spec.id} spec={spec} />;
      default:
        return null;
    }
  });
}

function TextResultAttribute({ spec }: { spec: AttributeSpec }) {
  const { control } = useFormContext<FormValues>();
  const { result } = React.useContext(ResultFormContext);
  const attributeIndex = React.useMemo(() => {
    return result.testResultSpec.attributeSpecs.findIndex(
      (s) => s.id === spec.id,
    );
  }, [result.testResultSpec.attributeSpecs, spec.id]);

  return (
    <Controller
      control={control}
      name={`attributes.${attributeIndex}.textValue`}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          value={field.value || ""}
          validationBehavior="aria"
          isInvalid={!!error}
          isRequired={spec.isRequired}
        >
          <Label>{spec.name}</Label>
          <Input />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function NumericResultAttribute({ spec }: { spec: AttributeSpec }) {
  const { control } = useFormContext<FormValues>();
  const { result } = React.useContext(ResultFormContext);
  const attributeIndex = React.useMemo(() => {
    return result.testResultSpec.attributeSpecs.findIndex(
      (s) => s.id === spec.id,
    );
  }, [result.testResultSpec.attributeSpecs, spec.id]);

  return (
    <Controller
      control={control}
      name={`attributes.${attributeIndex}.numericValue`}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          value={field.value?.toString() || ""}
          onChange={(value) =>
            field.onChange(value ? new Decimal(value) : null)
          }
          validationBehavior="aria"
          isInvalid={!!error}
          isRequired={spec.isRequired}
          type="number"
        >
          <Label>{spec.name}</Label>
          <Input type="number" />
          <FieldError>{error?.message}</FieldError>
        </TextField>
      )}
    />
  );
}

function BooleanResultAttribute({ spec }: { spec: AttributeSpec }) {
  const { control } = useFormContext<FormValues>();
  const { result } = React.useContext(ResultFormContext);
  const attributeIndex = React.useMemo(() => {
    return result.testResultSpec.attributeSpecs.findIndex(
      (s) => s.id === spec.id,
    );
  }, [result.testResultSpec.attributeSpecs, spec.id]);

  return (
    <Controller
      control={control}
      name={`attributes.${attributeIndex}.booleanValue`}
      render={({ field, fieldState: { error } }) => {
        const { value, ...fieldProps } = field;
        return (
          <div className="flex items-center gap-2">
            <Checkbox
              {...fieldProps}
              isSelected={value || false}
              onChange={field.onChange}
              isInvalid={!!error}
              isRequired={spec.isRequired}
            >
              {spec.name}
            </Checkbox>
            {error && <FieldError>{error.message}</FieldError>}
          </div>
        );
      }}
    />
  );
}
